// Boot-time wiring for the commerce billing UI ports. These
// members are reached by non-settings callers — global modals, upgrade flows —
// so they must be live before any lazy settings screen renders. Imported at
// module scope in `_app.tsx`, right after `commerce-data-wiring`.
import { trialExtensionModalOpenAtom } from '@/atoms/freeTrialModalAtoms';
import {
  clinicianMaxUpgradeModalEntryPointAtom,
  clinicianUpgradeModalOpenAtom,
  practiceUpgradeModalOpenAtom,
  trialModalPreviewDaysLeftAtom,
  upgradeModalFromTrialRecapAtom,
  usePaywallStates,
} from '@/atoms/paywallAtoms';
import { AppleIapInfoText } from '@/components/apple-iap-info-text';
import { ExternalLink } from '@/components/common/external-link';
import {
  BILLING_PLANS_PRICING_URL,
  FREE_VS_PRO_PLANS_ACCOUNT_ANCHOR_HELP_ARTICLE_PATH,
  GET_IN_TOUCH_URL,
  getBillingHelpCentreUrl,
  getBillingPlansPricingUrl,
  getComparePlansUrl,
  getGetInTouchUrl,
  getUsageLimitsHelpUrl,
} from '@/components/constants/billing-urls';
import { useIsGamifyTrialExtensionEntryEnabled } from '@/components/layout/Navigation/use-is-gamify-trial-extension-entry-enabled';
import { HeidiUpgradeBaseModal } from '@/components/modal/heidi-upgrade-base-modal/heidi-upgrade-base-modal';
import { PriceDisplay } from '@/components/modal/heidi-upgrade-base-modal/price-display';
import { USAGE_POLICY } from '@/components/utils/legalDocuments';
import {
  IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED,
  IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED,
  IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED,
  IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED,
  useIsEvidenceQueryLimitsForLegacyTiersEnabled,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsHeidi2OptInEnabled,
  useIsHeidi2PracticeMaxUpgradeEnabled,
  useIsHeidi2PricingPlansModalEnabled,
  useIsHeidi2TeamUpgradesEnabled,
  useIsWorkspaceSettingsIaEnabled,
} from '@/features/flags';
import { useIsGamifyTrialArmAllowed } from '@/features/post-note-quality/hooks/use-is-gamify-trial-arm-allowed';
import { resolveClinicianTrialRecapTestimonialVariant } from '@/features/trialsAndUpgrades/clinician-trial-recap-testimonial';
import { UpgradeTrialFeatureList } from '@/features/trialsAndUpgrades/ninety-day-end-of-trial/upgrade-trial-feature-list';
import { useNinetyDayTrialVariant } from '@/features/trialsAndUpgrades/ninety-day-end-of-trial/useNinetyDayTrialVariant';
import { TeamTrialUpgradeRequestButton } from '@/features/trialsAndUpgrades/team-trial-upgrade-request-button/team-trial-upgrade-request-button';
import { getPlanChangesHelpArticleUrl } from '@/features/trialsAndUpgrades/trial-recap-modal/utils';
import { useRegion } from '@/helpers/getRegion';
import { getTermsOfUseLink } from '@/helpers/localiseHelper';
import { useManualExposure } from '@/hooks/amplitude';
import { useMutateApplySubscriptionDiscount } from '@/hooks/mutation/useMutateApplySubscriptionDiscount';
import { useMutateCheckoutV2 } from '@/hooks/mutation/useMutateCheckoutV2';
import { useQueryCreditAllowance } from '@/hooks/query/useQueryCreditAllowance';
import { useQueryGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import {
  formatPriceWithCurrency,
  getCurrencySymbol,
  getDisplayedPriceFromPlan,
  getNewProductTierProduct,
  getPlanFromPeriod,
  getProductByName,
  getProductUpgradeDisplayPrices,
  getTierProduct,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQueryStripeLink } from '@/hooks/query/useQueryStripeLink';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useEvidenceBillingInfo } from '@/hooks/useEvidenceBillingInfo';
import { useFeatureLists } from '@/hooks/useFeatureLists';
import { useShouldHideMarketRestrictedFeatures } from '@/hooks/useShouldHideMarketRestrictedFeatures';
import {
  getCurrentPlanNameForTrial,
  useUpgradeModalTrialText,
} from '@/hooks/useUpgradeModalTrialText';
import { useUserQuota } from '@/hooks/useUserQuota';
import { track, type TrackEvent } from '@/lib/analytics/track';
import { sleep } from '@/lib/utils';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { PaywallSource } from '@/models/analytics/features/payments';
import { trackPracticeUpgradeModalContactSalesClick } from '@/models/analytics/features/trackNinetyDayTrialAnalytics';
import {
  trackSettingsBillingTeamUpgradeRequestSubmit,
  trackUpgradeToPracticeModalTeamUpgradeRequestSubmit,
} from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import { isTauri, openUrlInTauri } from '@/tauri/utils';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import { isOnPrem } from '@/utils/buildProfile';
import { getStripeCallbackRedirect, getStripeReturnRedirect } from '@/utils/scribeHTTP';
import { configureCommerceBillingUi, type CommerceBillingUiPorts } from '@heidi/commerce';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

configureCommerceBillingUi({
  AppleIapInfoText:
    AppleIapInfoText as unknown as CommerceBillingUiPorts['AppleIapInfoText'],
  ExternalLink,
  HeidiUpgradeBaseModal:
    HeidiUpgradeBaseModal as unknown as CommerceBillingUiPorts['HeidiUpgradeBaseModal'],
  PriceDisplay: PriceDisplay as unknown as CommerceBillingUiPorts['PriceDisplay'],
  UpgradeTrialFeatureList:
    UpgradeTrialFeatureList as unknown as CommerceBillingUiPorts['UpgradeTrialFeatureList'],
  TeamTrialUpgradeRequestButton:
    TeamTrialUpgradeRequestButton as unknown as CommerceBillingUiPorts['TeamTrialUpgradeRequestButton'],
  useTrialExtensionModalOpenValue: () => useAtomValue(trialExtensionModalOpenAtom),
  useSetTrialExtensionModalOpen: () => useSetAtom(trialExtensionModalOpenAtom),
  usePracticeUpgradeModalState: () => useAtom(practiceUpgradeModalOpenAtom),
  useSetPracticeUpgradeModalOpen: () => useSetAtom(practiceUpgradeModalOpenAtom),
  useUpgradeModalFromTrialRecapState: () => useAtom(upgradeModalFromTrialRecapAtom),
  useTrialModalPreviewDaysLeftValue: () => useAtomValue(trialModalPreviewDaysLeftAtom),
  useSetTrialModalPreviewDaysLeft: () => useSetAtom(trialModalPreviewDaysLeftAtom),
  useSetClinicianMaxUpgradeModalEntryPoint: () =>
    useSetAtom(clinicianMaxUpgradeModalEntryPointAtom) as unknown as (
      entryPoint: string | null
    ) => void,
  useSetClinicianUpgradeModalOpen: () => useSetAtom(clinicianUpgradeModalOpenAtom),
  usePaywallStates,
  isBillingCustomDowngradeFlowsEnabled: () =>
    IS_BILLING_CUSTOM_DOWNGRADE_FLOWS_ENABLED() === true,
  isEndOf90DayTrialExperienceEnabled: () =>
    IS_END_OF_90_DAY_TRIAL_EXPERIENCE_ENABLED() === true,
  isEvidenceLaunchTrialAndUpgradeFlowsEnabled: () =>
    IS_EVIDENCE_LAUNCH_TRIAL_AND_UPGRADE_FLOWS_ENABLED() === true,
  isEvidenceLaunchBillingPageRedesignEnabled: () =>
    IS_EVIDENCE_LAUNCH_BILLING_PAGE_REDESIGN_ENABLED() === true,
  useIsHeidi2PricingPlansModalEnabled,
  useIsHeidi2TeamUpgradesEnabled,
  useIsHeidi2PracticeMaxUpgradeEnabled,
  useIsHeidi2OptInEnabled,
  useIsHeidi2FullUpgradeFlowEnabled,
  useIsEvidenceQueryLimitsForLegacyTiersEnabled,
  useIsWorkspaceSettingsIaEnabled,
  useIsGamifyTrialExtensionEntryEnabled,
  useIsGamifyTrialArmAllowed,
  USAGE_POLICY,
  billingUrls: {
    getUsageLimitsHelpUrl,
    getBillingHelpCentreUrl,
    getBillingPlansPricingUrl,
    BILLING_PLANS_PRICING_URL,
    getGetInTouchUrl,
    GET_IN_TOUCH_URL,
    getComparePlansUrl,
    FREE_VS_PRO_PLANS_ACCOUNT_ANCHOR_HELP_ARTICLE_PATH,
  },
  getTermsOfUseLink,
  useMutateApplySubscriptionDiscount:
    useMutateApplySubscriptionDiscount as unknown as CommerceBillingUiPorts['useMutateApplySubscriptionDiscount'],
  useMutateCheckoutV2:
    useMutateCheckoutV2 as unknown as CommerceBillingUiPorts['useMutateCheckoutV2'],
  useQueryCreditAllowance:
    useQueryCreditAllowance as unknown as CommerceBillingUiPorts['useQueryCreditAllowance'],
  useQueryGamifyTrialExtension:
    useQueryGamifyTrialExtension as unknown as CommerceBillingUiPorts['useQueryGamifyTrialExtension'],
  products: {
    useQueryProducts,
    formatPriceWithCurrency,
    getCurrencySymbol,
    getDisplayedPriceFromPlan,
    getNewProductTierProduct,
    getPlanFromPeriod,
    getProductByName,
    getProductUpgradeDisplayPrices,
    getTierProduct,
  } as unknown as CommerceBillingUiPorts['products'],
  useQueryStripeLink:
    useQueryStripeLink as unknown as CommerceBillingUiPorts['useQueryStripeLink'],
  useQueryUser: useQueryUser as CommerceBillingUiPorts['useQueryUser'],
  useEvidenceBillingInfo:
    useEvidenceBillingInfo as unknown as CommerceBillingUiPorts['useEvidenceBillingInfo'],
  useFeatureLists,
  useShouldHideMarketRestrictedFeatures,
  useUpgradeModalTrialText:
    useUpgradeModalTrialText as unknown as CommerceBillingUiPorts['useUpgradeModalTrialText'],
  getCurrentPlanNameForTrial,
  useNinetyDayTrialVariant:
    useNinetyDayTrialVariant as unknown as CommerceBillingUiPorts['useNinetyDayTrialVariant'],
  useUserQuota: useUserQuota as unknown as CommerceBillingUiPorts['useUserQuota'],
  useRegion,
  resolveClinicianTrialRecapTestimonialVariant,
  getPlanChangesHelpArticleUrl,
  isAppleCompliantBuild,
  isOnPrem,
  isTauri,
  openUrlInTauri: (url: string) => {
    void openUrlInTauri(url);
  },
  useAppRouter: useRouter,
  track: (event) => track(event as TrackEvent),
  trackingEventName: TrackingEventName,
  paywallSource: PaywallSource,
  useManualExposure:
    useManualExposure as unknown as CommerceBillingUiPorts['useManualExposure'],
  trackPracticeUpgradeModalContactSalesClick,
  trackSettingsBillingTeamUpgradeRequestSubmit,
  trackUpgradeToPracticeModalTeamUpgradeRequestSubmit,
  sleep,
  getStripeCallbackRedirect,
  getStripeReturnRedirect,
});
